import type { HeatmapCell } from '../utils/financialCalculations';
import type { FinancialParams } from '../utils/financialDefaults';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const AXIS_OPTIONS: { key: keyof FinancialParams; label: string }[] = [
  { key: 'chargingTariff', label: 'Laadtarief (€/kWh)' },
  { key: 'bessCapex', label: 'BESS CAPEX (€/kWh)' },
  { key: 'pvCapex', label: 'PV CAPEX (€/kWp)' },
  { key: 'wacc', label: 'WACC (%)' },
  { key: 'gridImportTariff', label: 'Grid Import (€/kWh)' },
  { key: 'gridExportTariff', label: 'Grid Export (€/kWh)' },
  { key: 'sdeSubsidy', label: 'SDE++ (€/kWh)' },
  { key: 'debtRatio', label: 'Schuldratio (%)' },
  { key: 'interestRate', label: 'Rente (%)' },
];

const labelMap: Record<string, string> = Object.fromEntries(AXIS_OPTIONS.map(o => [o.key, o.label]));

function getColor(npv: number, minNpv: number, maxNpv: number): string {
  if (maxNpv === minNpv) return '#94A3B8';
  const ratio = (npv - minNpv) / (maxNpv - minNpv);
  if (ratio < 0.5) { return `rgb(220,${Math.round(50 + ratio * 2 * 180)},50)`; }
  return `rgb(${Math.round(220 - (ratio - 0.5) * 2 * 180)},200,50)`;
}

function fmtNl(v: number): string {
  if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(1).replace('.', ',')}M`;
  if (Math.abs(v) >= 1e3) return `${Math.round(v / 1e3).toLocaleString('nl-NL')}k`;
  return v.toLocaleString('nl-NL', { maximumFractionDigits: 0 });
}

export default function SensitivityHeatmap({ cells, xValues, yValues, xLabel, yLabel, onAxisChange }: {
  cells: HeatmapCell[]; xValues: number[]; yValues: number[]; xLabel: string; yLabel: string;
  onAxisChange?: (xKey: keyof FinancialParams, yKey: keyof FinancialParams) => void;
}) {
  const allNpvs = cells.map(c => c.npv);
  const minNpv = Math.min(...allNpvs);
  const maxNpv = Math.max(...allNpvs);
  const cols = xValues.length;

  return (
    <div className="bg-white rounded-xl border border-[#D9E2EC] p-4 shadow-sm mb-4">
      <div className="text-xs font-bold text-[#0B1F4A] mb-1">🗺️ Gevoeligheidsmatrix (NPV Heatmap)</div>
      {onAxisChange ? (
        <div className="flex items-center gap-2 mb-3 flex-wrap print:hidden">
          <div className="flex items-center gap-1">
            <span className="text-[9px] text-[#8899AA]">X-as:</span>
            <Select value={xLabel} onValueChange={(v) => onAxisChange(v as keyof FinancialParams, yLabel as keyof FinancialParams)}>
              <SelectTrigger className="h-7 w-[160px] text-[10px]"><SelectValue /></SelectTrigger>
              <SelectContent>{AXIS_OPTIONS.filter(o => o.key !== yLabel).map(o => <SelectItem key={o.key} value={o.key} className="text-[11px]">{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <span className="text-[9px] text-[#8899AA]">×</span>
          <div className="flex items-center gap-1">
            <span className="text-[9px] text-[#8899AA]">Y-as:</span>
            <Select value={yLabel} onValueChange={(v) => onAxisChange(xLabel as keyof FinancialParams, v as keyof FinancialParams)}>
              <SelectTrigger className="h-7 w-[160px] text-[10px]"><SelectValue /></SelectTrigger>
              <SelectContent>{AXIS_OPTIONS.filter(o => o.key !== xLabel).map(o => <SelectItem key={o.key} value={o.key} className="text-[11px]">{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <button onClick={() => onAxisChange('chargingTariff', 'bessCapex')} className="h-7 px-2.5 text-[10px] font-semibold rounded-md border border-[#D9E2EC] bg-[#F5F8FB] text-[#8899AA] hover:bg-[#EEF2F7] cursor-pointer">↺ Reset</button>
        </div>
      ) : <div className="text-[10px] text-[#8899AA] mb-3">{labelMap[xLabel] || xLabel} × {labelMap[yLabel] || yLabel} → NPV</div>}
      <div className="overflow-x-auto">
        <table className="border-collapse mx-auto">
          <thead><tr><th className="p-1 text-[9px] text-[#8899AA]">{labelMap[yLabel] || yLabel} ↓</th>{xValues.map((x, i) => <th key={i} className="p-1 text-[9px] text-[#3D4F6F] font-semibold text-center min-w-[52px]">{x >= 1000 ? `${(x / 1000).toFixed(0)}k` : x.toFixed(2)}</th>)}</tr></thead>
          <tbody>{yValues.map((y, yi) => (
            <tr key={yi}><td className="p-1 text-[9px] text-[#3D4F6F] font-semibold pr-2">{y >= 1000 ? `${(y / 1000).toFixed(0)}k` : y.toFixed(2)}</td>
            {xValues.map((_, xi) => { const cell = cells[yi * cols + xi]; return (
              <td key={xi} className="p-0.5"><div className="rounded text-center py-1.5 px-1 text-[9px] font-bold text-white" style={{ background: getColor(cell.npv, minNpv, maxNpv), minWidth: 48 }} title={`NPV: €${fmtNl(cell.npv)}`}>€{fmtNl(cell.npv)}</div></td>
            ); })}</tr>
          ))}</tbody>
        </table>
      </div>
      <div className="flex justify-center items-center gap-2 mt-3">
        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded" style={{ background: 'rgb(220,50,50)' }} /><span className="text-[9px] text-[#8899AA]">Negatief</span></div>
        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded" style={{ background: 'rgb(220,230,50)' }} /><span className="text-[9px] text-[#8899AA]">Break-even</span></div>
        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded" style={{ background: 'rgb(40,200,50)' }} /><span className="text-[9px] text-[#8899AA]">Positief</span></div>
      </div>
    </div>
  );
}
